import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {
    ClientIntegration,
    IntegrationOptionType,
} from 'app/modules/settings/interfaces/client-integration';
import { ClientIntegrationType } from 'app/modules/settings/interfaces/client-integration-type';
import { ClientIntegrationTypeService } from 'app/modules/settings/services/client-integration-type.service';
import { ClientIntegrationService } from 'app/modules/settings/services/client-integration.service';
import { ClientSubscriptionType } from 'app/modules/settings/services/client-subscription.service';
import { HierarchyService } from 'app/modules/settings/services/hierarchy.service';
import { IntegrationSourceHelper } from 'app/modules/shared/classes/client-data';
import {
    ClientInformationInterface,
    ClientInformationSources,
    IntegrationSource,
} from 'app/modules/shared/interfaces/client-data';
import { EmployeeService } from 'app/modules/shared/services/employee.service';
import { EditRequirementsValidators } from 'app/modules/shared/Validators/client-edit-requirements.validator';

@Component({
    selector: 'client-edit-integrations',
    templateUrl: './client-edit-integrations.component.html',
    styleUrls: [
        '../../client-edit-screen.component.scss',
        './client-edit-integrations.component.scss',
    ],
})
export class ClientEditIntegrationsComponent implements OnInit, OnChanges {
    initControlsFlag: boolean = false;
    subscriptionTypeList: ClientSubscriptionType[];

    @Input() form: UntypedFormGroup;
    @Input() clientModel: ClientInformationInterface;
    @Input() clientInfoSources: ClientInformationSources;
    @Input() action: string;
    @Input() isNVLite: boolean;
    @Output() formGroupEvent = new EventEmitter<UntypedFormGroup>();

    integrationSourceList: IntegrationSource[];
    currentIntegrations: ClientIntegration[];
    clientIntegrationTypes: ClientIntegrationType[];
    integrations: UntypedFormGroup;
    integrationSettings: UntypedFormGroup;
    integrationsArray: UntypedFormArray;
    integrationOptions: Array<IntegrationOptionType>;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private clientIntegrationService: ClientIntegrationService,
        private clientIntegrationTypeService: ClientIntegrationTypeService,
        private employeeService: EmployeeService,
        private hierarchyService: HierarchyService
    ) {}

    ngOnInit() {
        this.integrationOptions = [
            IntegrationOptionType.Compensafe,
            IntegrationOptionType.LosTalker,
            IntegrationOptionType.None,
        ];
        this.constructGroup();
    }

    ngOnChanges(changes: SimpleChanges) {
        setTimeout(() => {
            let hasControls =
                changes['form']?.currentValue?.controls?.hasOwnProperty('integrations');
            if (hasControls || changes.isNVLite) {
                this.initControls();
                this.patchValues();
                this.initControlsFlag = true;
            }
        }, 0);
    }

    constructGroup() {
        let group: UntypedFormGroup = this.formBuilder.group({
            integrationSettings: this.formBuilder.group({
                integrationsArray: this.formBuilder.array([]),
            }),
        });

        this.formGroupEvent.emit(group);
    }

    initControls() {
        this.integrations = this.form.get('integrations') as UntypedFormGroup;
        this.integrationSettings = this.integrations.get('integrationSettings') as UntypedFormGroup;
        this.integrationsArray = this.integrationSettings.get(
            'integrationsArray'
        ) as UntypedFormArray;
    }

    getPageData() {
        if (this.action != 'Add') {
            // Get all employees
            this.employeeService
                .doEmployeesExistForClient(this.clientModel.clientDetails.shortName)
                .subscribe(
                    (data) => {
                        if (data) {
                            this.integrationsArray.controls.forEach((array) => {
                                array.get('zeroPaddedSelect').disable();
                                array.get('zeroPaddedLen').disable();
                            });
                        }
                    },
                    (error) => {
                        throw new Error(error);
                    }
                );

            // Get all hierarchy entities
            this.hierarchyService
                .doHierarchyEntitiesExistForClient(this.clientModel.clientDetails.shortName)
                .subscribe(
                    (data) => {
                        if (data) {
                            this.integrationsArray.controls.forEach((array) => {
                                array.get('zeroPaddedSelect').disable();
                                array.get('zeroPaddedLen').disable();
                            });
                        }
                    },
                    (error) => {
                        throw new Error(error);
                    }
                );
        }
    }

    setIntegrationTypes() {
        this.clientIntegrationTypeService.getClientIntegrationTypes().subscribe(
            (data) => {
                this.clientIntegrationTypes = data;
                if (this.action !== 'Add') {
                    this.setIntegrationFields();
                } else {
                    this.addIntegrationsForSource();
                }
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    patchValues() {
        if (this.action !== 'Add') {
            this.clientInfoSources = IntegrationSourceHelper.setupSources(this.clientModel); // Populate a helper data model for patching the form controls
        } else {
            this.clientModel.clientIntegrationSources = [];
        }
        this.setIntegrationTypes();

        if (this.action !== 'Add') {
            this.integrations.patchValue(this.clientInfoSources);
        }
    }

    patchIntegrations() {
        this.integrationsArray.clear();

        if (this.action != 'Add') {
            let clientKeyValue = this.form.get('clientDetails').get('clientKey').value;
            this.clientIntegrationService.getClientIntegrations(clientKeyValue).subscribe(
                (data) => {
                    this.currentIntegrations = data;
                    this.populateIntegrationArray();
                },
                (error) => {
                    throw new Error(error);
                }
            );
        } else {
            this.populateIntegrationArray();
        }
    }

    populateIntegrationArray() {
        this.clientIntegrationTypes.forEach((integrationType) => {
            let currentIntegration = this.currentIntegrations.find(
                (integration) =>
                    integrationType.clientIntegrationTypeKey == integration.integrationTypeKey
            );

            if (currentIntegration) {
                let integrationValues = this.patchIntegrationValues(
                    currentIntegration,
                    integrationType
                );

                if (
                    (this.isNVLite && integrationType?.integrationType === 'Loans') ||
                    this.isNVLite === null ||
                    this.isNVLite === false
                ) {
                    this.integrationsArray.push(integrationValues);
                }
            }
        });
    }

    patchIntegrationValues(
        integration: ClientIntegration,
        clientIntegrationType: ClientIntegrationType
    ) {
        let ingestOption = integration.integrationActive ? 'Yes' : 'No';
        let isLoansIngest = clientIntegrationType.integrationType === 'Loans';
        if (isLoansIngest) {
            let integrationOption = integration.integrationOption;
            if (!integrationOption || (integrationOption && integrationOption.toString() === '')) {
                ingestOption = 'None';
            } else {
                ingestOption = integration.integrationOption?.toString();
            }
        }

        let zeroPaddedActive = integration.zeroPaddedIdentifier ? 'Yes' : 'No';
        let new_Records_Only = integration.new_Records_Only ? 'Ignore' : 'Update';
        let compensafeExport = integration.compensafeExport ? 'Yes' : 'No';

        return this.formBuilder.group({
            clientIntegrationKey: [integration.clientIntegrationKey],
            clientIntegrationType: [clientIntegrationType.integrationType],
            integrationTypeKey: [],
            ingestSelect: [ingestOption],
            isLoansIngest: isLoansIngest,
            csExportSelect: [compensafeExport],
            xmlExistingSelect: [new_Records_Only],
            zeroPaddedSelect: [zeroPaddedActive],
            zeroPaddedLen: [integration.zeroPaddedLen, EditRequirementsValidators.ZeroPadding()],
            useZeroPaddedSelect: [clientIntegrationType.useZeroPadding],
            useCSExportSelect: [clientIntegrationType.useCompensafeExport],
            useXMLExistingSelect: [clientIntegrationType.new_Records_Only],
        });
    }

    filteredBranchSources() {
        if (this.integrationSourceList && this.clientModel.clientIntegrationSources) {
            let sourceTypeList: string[] = this.integrationSourceList
                .filter((source) => source.canBePrimaryForBranch == true)
                .map((source) => source.integrationSource); // Filter out possible primary source types

            return this.clientModel.clientIntegrationSources.filter(
                (source) =>
                    sourceTypeList.includes(source.source) &&
                    !source.isAlternateForEmployeeIdentifier
            );
        }
    }

    filteredEmployeeSources() {
        if (this.integrationSourceList && this.clientModel.clientIntegrationSources) {
            let sourceTypeList: string[] = this.integrationSourceList
                .filter((source) => source.canBePrimaryForEmployee == true)
                .map((source) => source.integrationSource); // Filter out possible primary source types

            return this.clientModel.clientIntegrationSources.filter(
                (source) =>
                    sourceTypeList.includes(source.source) &&
                    !source.isAlternateForEmployeeIdentifier
            );
        }
    }

    addIntegrationsForSource() {
        let clientIntegration: ClientIntegration;

        this.currentIntegrations = [];
        this.clientIntegrationTypes.forEach((type) => {
            clientIntegration = {
                clientIntegrationKey: 0,
                integrationActive: false,
                integrationTypeKey: type.clientIntegrationTypeKey,
                zeroPaddedIdentifier: false,
                zeroPaddedLen: null,
                new_Records_Only: false,
                compensafeExport: false,
                isNew: true,
            };

            this.currentIntegrations.push(clientIntegration);
        });

        this.setIntegrationFields();
    }

    setIntegrationFields() {
        this.patchIntegrations();
        this.getPageData();
    }

    getIngestTitle(integration: any) {
        let inputWord = this.getInputWord(integration);
        if (inputWord.charAt(inputWord.length - 1) == 's') {
            inputWord = inputWord.slice(0, -1);
        }
        let yes = 'yes';
        let no = 'no';
        let isLoanIngest = integration?.controls?.clientIntegrationType?.value === 'Loans';
        if (isLoanIngest) {
            yes = 'Compensafe or LOS Talker';
            no = 'None';
        }
        let tooltipTitle = `If ${yes} is selected, all inbound ${inputWord} XML messages will be processed. \
             Choose ${no} to stop processing inbound XML messages.`;
        return tooltipTitle;
    }

    getZeroPaddedIdentifierTitle(integration: any) {
        let inputWord = this.getInputWord(integration);
        if (inputWord.charAt(inputWord.length - 1) == 's') {
            inputWord = inputWord.slice(0, -1);
        }
        let tooltipTitle = `If yes is selected, ${inputWord} identifiers will be zero padded to minimum length. \
                            A field will appear to define this length.`;
        return tooltipTitle;
    }

    getCSExportTitle(integration: any) {
        let inputWord = this.getInputWord(integration);
        let tooltipTitle = `If yes is selected, Mortgage Analytics will look to CompenSafe on a nightly basis to import ${inputWord}.`;
        return tooltipTitle;
    }

    getXMLExistingSelectTitle(integration: any) {
        let inputWord = this.getInputWord(integration);
        if (inputWord.charAt(inputWord.length - 1) == 's') {
            inputWord = inputWord.slice(0, -1);
        }
        let tooltipTitle = `If ignore is selected, we'll only do nothing when an XML message is received with an ${inputWord} \
                            identifier that is already in the database. If Update is selected, we'll update that record according to the message.`;
        return tooltipTitle;
    }

    getInputWord(integration: any) {
        let integrationType = integration.controls.clientIntegrationType.value;
        let inputWord = integrationType;
        return inputWord;
    }
}
